/**
 * SubtitleEditorV2.js — root V2 component
 *
 * Replicates SubtitleEditor.js business logic verbatim,
 * with targeted V2 additions:
 *  - EditorLayout three-panel resizable layout
 *  - VideoPanel wrapping Player.js (speed, HLS, follow-playhead toggle)
 *  - SubtitlesV2 replacing Subtitles.js (nudge buttons, insert button, followPlayhead)
 *  - ValidationBadge in toolbar
 *  - KeyboardShortcutsModal
 *  - Snackbar for replace-all feedback
 *  - Debounced localStorage writes (2s) to reduce main-thread pressure
 *  - Single Ctrl+Z registration (original has it duplicated)
 *  - followPlayhead state (default true)
 *  - onBeforeSave validation before save button fires
 *
 * Original files NOT touched:
 *  SubtitleEditor.js, SubtitleTimeSection.js, and all files in src/folders/
 */

import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import LockIcon from '@mui/icons-material/Lock';
import { debounce } from 'lodash';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import NotificationSystem from 'react-notification-system';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import GlobalStyle from '../../GlobalStyle';
import Header from '../../folders/ToolBar';
import { uploadToBlobStorage } from '../../folders/subtitles/BlobVttFile';
import Footer from '../../folders/subtitles/Footer';
import Loading from '../../folders/subtitles/Loading';
import ProgressBar from '../../folders/subtitles/ProgressBar';
import Sub from '../../libs/Sub';
import { getSubNodesfromVtt, sub2vtt } from '../../libs/readSub/SubUtils';
import EditSessionService, { generateReadOnlyKey, generateSessionKey } from '../../services/EditSessionService';
import EditTrackingService from '../../services/EditTrackingService';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import logCollectionService from '../../services/LogCollectionService';
import videoCRUDServiceInstance from '../../services/VideoCRUDService';
import { getKeyCode } from '../../utils';
import AppConfig from '../../utils/AppConfig';
import { LanguageList } from '../../utils/LanguageNames';
import { subtitleEditorHelpItems } from '../../utils/helpItems';
import { readSubtitleFromStorage, writeSubtitleToStorage } from '../../utils/subtitleStorage';
import HelpOverlay from '../common/HelpOverlay';
import { fetchBlobUrl } from './ReadBlobToString';
import EditorLayout from './v2/EditorLayout';
import KeyboardShortcutsModal from './v2/KeyboardShortcutsModal';
import SubtitlesV2 from './v2/SubtitlesV2';
import ValidationBadge from './v2/ValidationBadge';
import VideoPanel from './v2/VideoPanel';

const StyleV2 = styled.div`
    display: flex;
    flex-direction: column;
    height: 100vh;
    width: 100%;
    overflow: hidden;

    .read-only-banner,
    .inactivity-warning-banner {
        position: sticky;
        top: 0;
        left: 0;
        right: 0;
        z-index: 1100;
        width: 100%;
    }

    .inactivity-warning-banner {
        position: fixed;
        animation: pulse 1s infinite alternate;
    }

    @keyframes pulse {
        from { opacity: 0.8; }
        to { opacity: 1; }
    }

    .toolbar-container {
        display: flex;
        align-items: center;
        padding: 5px 10px;
        background-color: rgba(30, 30, 30, 0.95);
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        overflow-x: auto;
        gap: 8px;
    }

    .toolbar-group {
        display: flex;
        align-items: center;
        margin-right: 8px;
        padding-right: 8px;
        border-right: 1px solid rgba(255, 255, 255, 0.1);
        white-space: nowrap;
        &:last-child { border-right: none; }
    }

    .toolbar-spacer { flex: 1; }

    .v2-layout-wrapper {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }
`;

export default function VideoPlayerAppV2({ defaultLang }) {
    useTrackUsage('SubtitleEditorV2', 'editor', 'subtitles');
    const subtitleHistory = useRef([]);
    const notificationSystem = useRef(null);
    const [player, setPlayer] = useState(null);
    const [loading, setLoading] = useState('');
    const [processing, setProcessing] = useState(0);
    const [language, setLanguage] = useState(defaultLang);
    const [subtitle, setSubtitleOriginal] = useState([]);
    const [waveform, setWaveform] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [currentIndex, setCurrentIndex] = useState(-1);
    const [translate, setTranslate] = useState('en');
    const [directUrl, setDirectUrl] = useState(null);
    const [viewEng, setViewEng] = useState(false);
    const [bookmarked, setBookmarked] = useState([]);
    const [subtitleComment, setSubtitleComment] = useState([]);
    const broadcastChannelRef = useRef(null);

    const [selectedLng, setSelectedLng] = useState(() => {
        const lang = window.history.state?.usr?.selectedLanguage;
        if (!lang) return 'English';
        try {
            const vp = JSON.parse(localStorage.getItem('videoProps'));
            if (vp?.videoLanguages?.some((l) => l.languageName === lang)) return lang;
        } catch (_) {}
        return 'English';
    });
    const [lngIndex, setLngIndex] = useState('0');

    const [searchTerm, setSearchTerm] = useState('');
    const [replaceTerm, setReplaceTerm] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [matchOffsets, setMatchOffsets] = useState([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

    // V2-only state
    const [followPlayhead, setFollowPlayhead] = useState(true);
    const [replaceAllCount, setReplaceAllCount] = useState(0);
    const [showReplaceToast, setShowReplaceToast] = useState(false);
    const [showShortcutsModal, setShowShortcutsModal] = useState(false);

    const newSub = useCallback((item) => new Sub(item), []);
    const hasSub = useCallback(
        (sub) => subtitle.findIndex((item) => item.start === sub.start && item.end === sub.end),
        [subtitle],
    );

    const [readOnly, setReadOnly] = useState(false);
    const [activeEditor, setActiveEditor] = useState(null);
    const keepAliveIntervalRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const [duplicateSessionDetected, setDuplicateSessionDetected] = useState(false);
    const [lastActivity, setLastActivity] = useState(Date.now());
    const inactivityTimeoutRef = useRef(null);
    const INACTIVITY_TIMEOUT = AppConfig.sessionInactivityTimeoutMinutes * 60 * 1000;
    const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
    const [sessionActive, setSessionActive] = useState(false);
    const [userLevel, setUserLevel] = useState('');
    const [mirroredGujaratiSubs, setMirroredGujaratiSubs] = useState([]);
    const [leftColumnLanguage, setLeftColumnLanguage] = useState('English');
    const [rightColumnLanguage, setRightColumnLanguage] = useState('Gujarati');
    const [showDualColumns, setShowDualColumns] = useState(false);

    // Always-current ref for selectedLng — updated synchronously during render.
    // Used by setSubtitle / setLanguageSubTitle so that any stale callback (e.g. one
    // captured inside an async mergeSubtitleWithEnglish before a language switch) still
    // writes to the CORRECT per-language storage key instead of the old one.
    const selectedLngRef = useRef(selectedLng);
    selectedLngRef.current = selectedLng;

    // V2: debounced storage write (per-video+language key)
    const debouncedLocalStorageSave = useRef(
        debounce((data, lang) => {
            try {
                writeSubtitleToStorage(data, lang);
            } catch (_) {}
        }, 2000),
    ).current;

    // When ToolBar is about to switch language it dispatches 'cancelSubtitleDebounce'.
    // We cancel the pending debounce — ToolBar already wrote a synchronous snapshot
    // of the current subtitle to the OLD language key before dispatching this event,
    // so no data is lost.  This prevents the debounce from firing later with a stale
    // (old-language) subtitle value captured against the new selectedLng.
    useEffect(() => {
        const cancelHandler = () => {
            try {
                if (debouncedLocalStorageSave && typeof debouncedLocalStorageSave.cancel === 'function') {
                    debouncedLocalStorageSave.cancel();
                    console.log('SubtitleEditorV2: debounced storage write cancelled before language switch');
                }
            } catch (e) {
                console.error('SubtitleEditorV2: error cancelling debouncedLocalStorageSave', e);
            }
        };
        window.addEventListener('cancelSubtitleDebounce', cancelHandler);
        return () => window.removeEventListener('cancelSubtitleDebounce', cancelHandler);
    }, [debouncedLocalStorageSave]);

    useEffect(() => {
        const handleResize = debounce(() => {
            setWindowSize({ width: window.innerWidth, height: window.innerHeight });
        }, 200);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchMirroredGujarati = async () => {
            if (selectedLng === 'English') {
                const gujratiVTTUrl = await getLanguageVTTFile('Gujarati');
                const vttData = await fetchBlobUrl(gujratiVTTUrl);
                if (vttData) {
                    setMirroredGujaratiSubs(getSubNodesfromVtt(vttData));
                } else {
                    setMirroredGujaratiSubs([]);
                }
            }
        };
        fetchMirroredGujarati();
    }, [selectedLng]);

    const updateMirroredGujaratiTimings = useCallback((newEnglishSubs) => {
        setMirroredGujaratiSubs((prevGujaratiSubs) =>
            newEnglishSubs.map((eng, i) => {
                const guj = prevGujaratiSubs[i];
                return {
                    ...((guj && { ...guj }) || {}),
                    start: eng.start,
                    end: eng.end,
                    Estart: eng.Estart,
                    Eend: eng.Eend,
                    text: guj ? guj.text : '',
                    text2: guj ? guj.text2 : '',
                    note: guj ? guj.note : '',
                    bookmark: guj ? guj.bookmark : false,
                };
            }),
        );
    }, []);

    const saveMirroredGujaratiVtt = useCallback(
        async (saveType = 'manual') => {
            const videoProps = JSON.parse(localStorage.getItem('videoProps'));
            if (videoProps?.mirroredTimestampVtt && mirroredGujaratiSubs.length > 0) {
                const vttContent = sub2vtt(mirroredGujaratiSubs, false);
                const gujaratiVttUrl = await getLanguageVTTFile('Gujarati');
                await uploadToBlobStorage(gujaratiVttUrl, vttContent, 'vtt');
                await videoCRUDServiceInstance.saveMetaDataforVideoId(videoProps.id, [], [], 'Gujarati', saveType);
            }
        },
        [mirroredGujaratiSubs],
    );

    const splitMirroredGujratiSubtitle = useCallback((index) => {
        setMirroredGujaratiSubs((prevSubs) => {
            if (!prevSubs || prevSubs.length === 0 || index < 0 || index >= prevSubs.length) return prevSubs;
            return [
                ...prevSubs.slice(0, index + 1),
                { ...prevSubs[index + 1], text: '', text2: '', note: '', bookmark: false },
                ...prevSubs.slice(index + 1),
            ];
        });
    }, []);

    const addMirroredGujaratiSub = useCallback((index, newSub) => {
        setMirroredGujaratiSubs((prevSubs) => {
            const newMirrored = { ...newSub, text: '', text2: '', note: '', bookmark: false };
            return [
                ...prevSubs.slice(0, index),
                newMirrored,
                ...prevSubs.slice(index).map((sub) => ({ ...sub })),
            ];
        });
    }, []);

    const handleUserActivity = useCallback(
        debounce(() => { setLastActivity(Date.now()); }, 500),
        [],
    );

    const startEditSession = useCallback(async (videoId, languageName) => {
        handleUserActivity();
        logCollectionService.logTaggedEvent(
            'subtitle_session_start_requested',
            { videoId, languageName },
            ['session', 'subtitle-editor'],
        );
        console.log('Starting edit session for', videoId, languageName);
        try {
            const authUser = JSON.parse(localStorage.getItem('authUser'));
            const authToken = JSON.parse(localStorage.getItem('authToken'));
            const videoProps = JSON.parse(localStorage.getItem('videoProps'));
            const readOnlyKey = `read_only_${videoId}_${languageName}`;
            sessionStorage.removeItem(readOnlyKey);

            const existingSession = await EditSessionService.checkForExistingSession(videoId, languageName);
            if (existingSession.exists) {
                logCollectionService.logTaggedEvent(
                    'subtitle_duplicate_session_detected',
                    { videoId, languageName },
                    ['session', 'subtitle-editor', 'duplicate-session'],
                );
                setReadOnly(true);
                setActiveEditor(authUser.email);
                setDuplicateSessionDetected(true);
                EditSessionService.markSessionAsReadOnly(videoId, languageName, existingSession.sessionId);
                Swal.fire({
                    title: 'Already Open',
                    html: `
                        <p>This file is already open in another tab or window. Please close the other instance to edit here.</p>
                        <p style="margin-top: 15px; font-size: 14px;">If you believe this is an error, please:</p>
                        <ol style="text-align: left; margin-top: 5px; font-size: 14px;">
                            <li>Go to <a href="/sessions/active" target="_blank" style="font-weight: bold; color: #3085d6;">Sessions Manager (It will open in another tab)</a></li>
                            <li>Close this window/tab or go back to the folder view </li>
                            <li>Review and delete any stale sessions in the Active Editing Sessions Page </li>
                            <li>Try opening this file again</li>
                        </ol>
                    `,
                    icon: 'warning',
                    confirmButtonText: 'OK',
                });
                return;
            }

            if (EditSessionService.isReadOnlySession(videoId, languageName)) {
                setReadOnly(true);
                setDuplicateSessionDetected(true);
                return;
            }

            const result = await EditSessionService.startEditSession(videoId, languageName, authUser.email, authToken, 'V2');
            setupBroadcastListener(videoId, languageName);

            if (result.status === 'SUCCESS' || result.status === 'RECONNECTED') {
                logCollectionService.logTaggedEvent(
                    'subtitle_session_started',
                    { videoId, languageName, status: result.status },
                    ['session', 'subtitle-editor'],
                );
                setReadOnly(false);
                setActiveEditor(authUser.email);
                if (keepAliveIntervalRef.current) {
                    clearInterval(keepAliveIntervalRef.current);
                    keepAliveIntervalRef.current = null;
                }
                keepAliveIntervalRef.current = setInterval(() => {
                    keepAliveEditSession(videoProps.id, languageName);
                }, 60000);
            } else if (result.status === 'DUPLICATE_SESSION') {
                logCollectionService.logTaggedEvent(
                    'subtitle_session_rejected_duplicate',
                    { videoId, languageName },
                    ['session', 'subtitle-editor', 'duplicate-session'],
                );
                setReadOnly(true);
                setActiveEditor(result.activeEditor);
                setDuplicateSessionDetected(true);
                EditSessionService.markSessionAsReadOnly(videoId, languageName, result.sessionId);
                Swal.fire({
                    title: 'Already Open',
                    html: `
                        <p>This file is already open in another tab or window. Please close the other instance to edit here.</p>
                        <p style="margin-top: 15px; font-size: 14px;">If you believe this is an error, please:</p>
                        <ol style="text-align: left; margin-top: 5px; font-size: 14px;">
                            <li>Go to <a href="/sessions/active" target="_blank" style="font-weight: bold; color: #3085d6;">Sessions Manager (It will open in another tab)</a></li>
                            <li>Close this window/tab or go back to the folder view </li>
                            <li>Review and delete any stale sessions in the Active Editing Sessions Page </li>
                            <li>Try opening this file again</li>
                        </ol>
                    `,
                    icon: 'warning',
                    confirmButtonText: 'OK',
                });
            } else if (result.status === 'LOCKED') {
                logCollectionService.logTaggedEvent(
                    'subtitle_session_locked',
                    { videoId, languageName, activeEditor: result.activeEditor },
                    ['session', 'subtitle-editor', 'read-only'],
                );
                setReadOnly(true);
                setActiveEditor(result.activeEditor);
                Swal.fire({
                    title: 'Read-Only Mode',
                    text: `This subtitle is currently being edited by ${result.activeEditor}. You can view it, but cannot make changes.`,
                    icon: 'info',
                    confirmButtonText: 'OK',
                });
            }
        } catch (error) {
            console.error('Error starting edit session:', error);
            logCollectionService.logTaggedEvent(
                'subtitle_session_start_failed',
                {
                    videoId,
                    languageName,
                    message: error?.message,
                },
                ['session', 'subtitle-editor', 'error'],
            );
            setReadOnly(true);
            setActiveEditor('The editing session could not be established. Your changes will not be saved. Please exit the video and try reopening it after a moment.');
        }
    });

    const setupBroadcastListener = useCallback((videoId, languageName) => {
        if (broadcastChannelRef.current) {
            broadcastChannelRef.current.close();
            broadcastChannelRef.current = null;
        }
        const channel = new BroadcastChannel(`amrutvaani-session-check-${videoId}-${languageName}`);
        broadcastChannelRef.current = channel;
        channel.onmessage = (event) => {
            if (event.data.checkSession) {
                const sessionKey = generateSessionKey(videoId, languageName);
                const sessionId = sessionStorage.getItem(sessionKey);
                if (sessionId) channel.postMessage({ hasSession: true, sessionId });
            }
        };
        return () => {
            if (broadcastChannelRef.current) {
                broadcastChannelRef.current.close();
                broadcastChannelRef.current = null;
            }
        };
    });

    const keepAliveEditSession = useCallback(async (videoId, languageName) => {
        if (duplicateSessionDetected) return;
        console.log('Keeping edit session alive for', videoId, languageName);
        try {
            const authUser = JSON.parse(localStorage.getItem('authUser'));
            const authToken = JSON.parse(localStorage.getItem('authToken'));
            await EditSessionService.keepAliveEditSession(videoId, languageName, authUser.email, authToken);
        } catch (error) {
            console.error('Error keeping edit session alive:', error);
            logCollectionService.logTaggedEvent(
                'subtitle_session_keepalive_failed',
                {
                    videoId,
                    languageName,
                    message: error?.message,
                },
                ['session', 'subtitle-editor', 'error'],
            );
        }
    });

    const cleanupSessionStorage = useCallback((videoId, language) => {
        if (videoId && language) {
            const sessionKey = generateSessionKey(videoId, language);
            const readOnlyKey = generateReadOnlyKey(videoId, language);
            sessionStorage.removeItem(sessionKey);
            sessionStorage.removeItem(readOnlyKey);
        }
        if (keepAliveIntervalRef.current) {
            clearInterval(keepAliveIntervalRef.current);
            keepAliveIntervalRef.current = null;
        }
    }, []);

    const endEditSession = useCallback(
        async (isUnloading = false) => {
            try {
                if (sessionActive) {
                    await EditTrackingService.endSession();
                    setSessionActive(false);
                }
                const videoProps = JSON.parse(localStorage.getItem('videoProps'));
                const authUser = JSON.parse(localStorage.getItem('authUser'));
                const authToken = JSON.parse(localStorage.getItem('authToken'));
                if (videoProps?.id && selectedLng) {
                    const previousLanguage = previousLanguageRef.current;
                    if (!duplicateSessionDetected && !readOnly && !EditSessionService.isReadOnlySession(videoProps.id, selectedLng)) {
                        if (isUnloading) {
                            await EditSessionService.endEditSessionWithBeacon(videoProps.id, selectedLng);
                            if (previousLanguage && previousLanguage !== selectedLng) {
                                await EditSessionService.endEditSessionWithBeacon(videoProps.id, previousLanguage);
                            }
                        } else {
                            await EditSessionService.endEditSession(videoProps.id, selectedLng, authUser.email, authToken);
                        }
                    }
                    cleanupSessionStorage(videoProps.id, selectedLng);
                    if (isUnloading) cleanupSessionStorage(videoProps.id, previousLanguage);
                    if (keepAliveIntervalRef.current) {
                        clearInterval(keepAliveIntervalRef.current);
                        keepAliveIntervalRef.current = null;
                    }
                }
            } catch (_) {
                try {
                    const videoProps = JSON.parse(localStorage.getItem('videoProps'));
                    if (videoProps?.id && selectedLng) cleanupSessionStorage(videoProps.id, selectedLng);
                } catch (_2) {}
            }
        },
        [duplicateSessionDetected, readOnly, selectedLng, cleanupSessionStorage, sessionActive],
    );

    useEffect(() => {
        return () => {
            endEditSession(true);
            if (broadcastChannelRef.current) {
                broadcastChannelRef.current.close();
                broadcastChannelRef.current = null;
            }
        };
    }, [endEditSession]);

    useEffect(() => {
        const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown'];
        activityEvents.forEach((event) => document.addEventListener(event, handleUserActivity));
        const checkInactivity = () => {
            const timeSinceLastActivity = Date.now() - lastActivity;
            if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
                logCollectionService.logTaggedEvent(
                    'subtitle_session_inactive_timeout',
                    {
                        timeSinceLastActivity,
                        threshold: INACTIVITY_TIMEOUT,
                    },
                    ['session', 'inactivity', 'subtitle-editor'],
                );
                endEditSession(true);
                Swal.fire({
                    title: 'Session Expired',
                    text: 'You have been inactive for 15 minutes.',
                    icon: 'info',
                    timer: 5000,
                    timerProgressBar: true,
                }).then(() => navigate('/'));
                if (inactivityTimeoutRef.current) clearInterval(inactivityTimeoutRef.current);
            }
        };
        inactivityTimeoutRef.current = setInterval(checkInactivity, 60000);
        return () => {
            activityEvents.forEach((event) => document.removeEventListener(event, handleUserActivity));
            if (inactivityTimeoutRef.current) clearInterval(inactivityTimeoutRef.current);
        };
    }, [lastActivity, handleUserActivity, navigate, endEditSession]);

    const cleanupAllSessions = useCallback(() => {
        const videoProps = JSON.parse(localStorage.getItem('videoProps'));
        if (videoProps?.id) {
            Object.keys(sessionStorage).forEach((key) => {
                if (key.startsWith(`edit_session_${videoProps.id}_`) || key.startsWith(`read_only_${videoProps.id}_`)) {
                    sessionStorage.removeItem(key);
                }
            });
        }
    }, []);

    const previousLanguageRef = useRef(null);
    const inMiddleofSessionEnding = useRef(false);

    const formatSub = useCallback(
        (sub) => {
            if (Array.isArray(sub)) return sub.map((item) => newSub(item));
            return newSub(sub);
        },
        [newSub],
    );

    const setLanguageSubTitle = useCallback(
        (newSubtitle, saveToHistory = true) => {
            if (!isEqual(newSubtitle, subtitle)) {
                if (saveToHistory) subtitleHistory.current = [];
                // Use selectedLngRef.current (not the closed-over selectedLng) so that any
                // stale callback (e.g. from an async mergeSubtitleWithEnglish captured before
                // a language switch) still writes to the CORRECT per-language key.
                debouncedLocalStorageSave(newSubtitle, selectedLngRef.current);
                setSubtitleOriginal(formatSub(newSubtitle));
                window.dispatchEvent(new Event('storage'));
            }
        },
        [subtitle, setSubtitleOriginal, formatSub, debouncedLocalStorageSave],
    );

    const setSubtitle = useCallback(
        (newSubtitle, saveToHistory = true, skipUpdateMirrored = false) => {
            if (!isEqual(newSubtitle, subtitle)) {
                if (saveToHistory) {
                    subtitleHistory.current.push({
                        subtitle: formatSub(subtitle),
                        mirroredGujaratiSubs: mirroredGujaratiSubs ? [...mirroredGujaratiSubs] : [],
                    });
                    limitHistorySize();
                }
                // Use selectedLngRef.current (not the closed-over selectedLng) so that any
                // stale callback still writes to the correct per-language key.
                debouncedLocalStorageSave(newSubtitle, selectedLngRef.current);
                setSubtitleOriginal(formatSub(newSubtitle));
                window.dispatchEvent(new Event('storage'));
                if (selectedLngRef.current === 'English' && !skipUpdateMirrored) {
                    updateMirroredGujaratiTimings(newSubtitle);
                }
            }
        },
        [subtitle, setSubtitleOriginal, formatSub, debouncedLocalStorageSave, mirroredGujaratiSubs, updateMirroredGujaratiTimings],
    );

    const handleLanguageChange = useCallback(async () => {
        const videoProps = JSON.parse(localStorage.getItem('videoProps'));
        if (!videoProps || !videoProps.id || !selectedLng) return;

        inMiddleofSessionEnding.current = true;
        if (sessionActive) {
            try {
                await EditTrackingService.endSession();
                setSessionActive(false);
            } catch (_) {}
        }

        if (videoProps?.id) {
            Object.keys(sessionStorage).forEach((key) => {
                if (key.startsWith(`edit_session_${videoProps.id}_`) || key.startsWith(`read_only_${videoProps.id}_`)) {
                    endEditSession();
                }
            });
        }
        await new Promise((resolve) => setTimeout(resolve, 500));

        logCollectionService.logTaggedEvent(
            'subtitle_language_change',
            {
                from: previousLanguageRef.current,
                to: selectedLng,
                videoId: videoProps.id,
            },
            ['language-change', 'subtitle-editor'],
        );

        if (selectedLng === 'English' || selectedLng === 'Gujarati') {
            setShowDualColumns(false);
        } else {
            setShowDualColumns(true);
            if (videoProps.videoLanguages.some((lang) => lang.languageName === 'English-VO')) {
                setLeftColumnLanguage(selectedLng === 'English-VO' ? 'English' : 'English-VO');
            } else {
                setLeftColumnLanguage('English');
            }
            setRightColumnLanguage(selectedLng);
        }

        if (selectedLng) {
            subtitleHistory.current = [];
            startEditSession(videoProps.id, selectedLng);
        }
        inMiddleofSessionEnding.current = false;
    }, [selectedLng, sessionActive, startEditSession, setShowDualColumns, setLeftColumnLanguage, setRightColumnLanguage, endEditSession]);

    const loadLeftColumnAsBase = useCallback(
        async (videoProps) => {
            if (!showDualColumns) return;
            try {
                const leftColumnLangIndex = videoProps.videoLanguages.findIndex(
                    (lang) => lang.languageName === leftColumnLanguage,
                );
                if (leftColumnLangIndex === -1) return;
                const leftColumnVttData = await fetchBlobUrl(videoProps.videoLanguages[leftColumnLangIndex].vttFileUrl);
                if (leftColumnVttData) {
                    const leftColumnSubtitles = getSubNodesfromVtt(leftColumnVttData);
                    setSubtitle(leftColumnSubtitles.map((item) => new Sub(item)), false);
                    return leftColumnSubtitles;
                }
            } catch (_) {}
        },
        [leftColumnLanguage, setSubtitle, showDualColumns],
    );

    const mergeRightColumnIntoBase = useCallback(
        async (videoProps, baseSubtitles = null) => {
            if (!showDualColumns) return;
            try {
                let leftColumnSubtitles = baseSubtitles;
                if (!leftColumnSubtitles) {
                    leftColumnSubtitles = subtitle.length > 0 ? subtitle : await loadLeftColumnAsBase(videoProps);
                }
                const rightColumnLangIndex = videoProps.videoLanguages.findIndex(
                    (lang) => lang.languageName === rightColumnLanguage,
                );
                if (rightColumnLangIndex === -1) return;
                const rightColumnVttData = await fetchBlobUrl(videoProps.videoLanguages[rightColumnLangIndex].vttFileUrl);
                if (rightColumnVttData) {
                    const rightColumnSubtitles = getSubNodesfromVtt(rightColumnVttData);
                    // Only include entries where the right column (English-VO) actually has a cue;
                    // extra base cues beyond the right column count are skipped to avoid blank cues.
                    const mergedSubtitles = leftColumnSubtitles
                        .slice(0, rightColumnSubtitles.length)
                        .map((baseSub, index) => {
                        const rightSub = rightColumnSubtitles[index];
                        return new Sub({
                            ...baseSub,
                            text: rightSub?.text || '',
                            text2: rightSub?.text2 || '',
                            Etext: baseSub.text || '',
                            Etext2: baseSub.text2 || '',
                            Estart: baseSub.start,
                            Eend: baseSub.end,
                            note: rightSub?.note || baseSub.note || '',
                            bookmark: rightSub?.bookmark || baseSub.bookmark || false,
                        });
                    });
                    setLanguageSubTitle(mergedSubtitles, false);
                }
            } catch (_) {}
        },
        [rightColumnLanguage, leftColumnLanguage, setLanguageSubTitle, showDualColumns, subtitle, loadLeftColumnAsBase],
    );

    const cleanupSession = useCallback(
        async (languageOverride = null) => {
            const videoProps = JSON.parse(localStorage.getItem('videoProps'));
            const languageToClose = languageOverride || previousLanguageRef.current || selectedLng;
            if (!videoProps || !videoProps.id || !languageToClose) return;
            try {
                if (sessionActive) {
                    await EditTrackingService.endSession();
                }
                await EditSessionService.endEditSessionWithBeacon(videoProps.id, languageToClose);
                cleanupSessionStorage(videoProps.id, languageToClose);
                endEditSession();
            } catch (_) {}
        },
        [selectedLng, sessionActive, cleanupSessionStorage, endEditSession],
    );

    useEffect(() => {
        const videoProps = JSON.parse(localStorage.getItem('videoProps'));
        if (!videoProps || !videoProps.id || !selectedLng) return;
        if (previousLanguageRef.current === selectedLng) return;
        if (inMiddleofSessionEnding.current) return;
        handleLanguageChange();
        return () => {
            if (keepAliveIntervalRef.current) {
                clearInterval(keepAliveIntervalRef.current);
                keepAliveIntervalRef.current = null;
            }
            const closingLanguage = previousLanguageRef.current || selectedLng;
            cleanupSession(closingLanguage);
            previousLanguageRef.current = selectedLng;
            inMiddleofSessionEnding.current = false;
        };
    }, [selectedLng, sessionActive, startEditSession, cleanupAllSessions]);

    useEffect(() => {
        const handleUnload = () => {
            cleanupBeforeUnload();
            endEditSession(true);
        };
        window.addEventListener('beforeunload', handleUnload);
        return () => window.removeEventListener('beforeunload', handleUnload);
    }, []);

    const cleanupBeforeUnload = () => {
        const videoProps = JSON.parse(localStorage.getItem('videoProps'));
        if (videoProps?.id && selectedLng) {
            const sessionKey = generateSessionKey(videoProps.id, selectedLng);
            const readOnlyKey = generateReadOnlyKey(videoProps.id, selectedLng);
            sessionStorage.removeItem(sessionKey);
            sessionStorage.removeItem(readOnlyKey);
            if (keepAliveIntervalRef.current) {
                clearInterval(keepAliveIntervalRef.current);
                keepAliveIntervalRef.current = null;
            }
        }
    };

    const copySubs = useCallback(() => formatSub(subtitle), [subtitle, formatSub]);

    const limitHistorySize = useCallback(() => {
        const MAX_HISTORY_SIZE = 50;
        if (subtitleHistory.current.length > MAX_HISTORY_SIZE) {
            subtitleHistory.current = subtitleHistory.current.slice(-MAX_HISTORY_SIZE);
        }
    }, []);

    const undoSubs = useCallback(() => {
        if (subtitleHistory.current.length === 0) return;
        const last = subtitleHistory.current.pop();
        if (last && last.subtitle && Array.isArray(last.subtitle) && last.subtitle.length > 0) {
            if (last.mirroredGujaratiSubs && last.mirroredGujaratiSubs.length === last.subtitle.length) {
                setMirroredGujaratiSubs(last.mirroredGujaratiSubs || []);
            }
            setSubtitle(formatSub(last.subtitle), false, true);
        }
    }, [setSubtitle, setMirroredGujaratiSubs, subtitleHistory]);

    const clearSubs = useCallback(() => {
        setSubtitle([]);
        subtitleHistory.current.length = 0;
    }, [setSubtitle, subtitleHistory]);

    const checkSub = useCallback(
        (sub) => {
            const s = sub instanceof Sub ? sub : new Sub(sub);
            
            const index = hasSub(s);
            if (index < 0) return;
            const previous = subtitle[index - 1];
            if (s.startTime === 0 && s.endTime === 0) return false;
            return (previous && s.startTime < previous.endTime) || !s.check || s.duration < 0.002;
        },
        [subtitle, hasSub],
    );

    const notify = useCallback(
        (obj) => {
            const notification = notificationSystem.current;
            notification.clearNotifications();
            notification.addNotification({
                position: 'tc',
                dismissible: 'none',
                autoDismiss: 2,
                message: obj.message,
                level: obj.level,
            });
        },
        [notificationSystem],
    );

    const removeSub = useCallback(
        (sub) => {
            const index = hasSub(sub);
            if (index < 0) return;
            const subs = copySubs();
            subs.splice(index, 1);
            setSubtitle(subs);
            EditTrackingService.trackEdit(index, 'delete', 'subtitle', sub?.text2 || sub?.text || '', '');
            if (selectedLng === 'English') {
                setMirroredGujaratiSubs((prevSubs) => {
                    if (!prevSubs || prevSubs.length === 0 || index < 0 || index >= prevSubs.length) return prevSubs;
                    const newSubs = prevSubs.slice(0, index).concat(prevSubs.slice(index + 1));
                    for (let i = index; i < newSubs.length; i++) {
                        newSubs[i] = {
                            ...newSubs[i],
                            text: prevSubs[i + 1]?.text ?? '',
                            text2: prevSubs[i + 1]?.text2 ?? '',
                            note: prevSubs[i + 1]?.note ?? '',
                            bookmark: prevSubs[i + 1]?.bookmark ?? false,
                        };
                    }
                    return newSubs;
                });
            }
        },
        [hasSub, copySubs, setSubtitle, selectedLng, setMirroredGujaratiSubs],
    );

    const addSub = useCallback(
        (index, sub) => {
            const subs = copySubs();
            subs.splice(index, 0, formatSub(sub));
            setSubtitle(subs);
            EditTrackingService.trackEdit(index, 'add', 'subtitle', '', sub?.text2 || sub?.text || '');
            if (selectedLng === 'English') addMirroredGujaratiSub(index, sub);
        },
        [copySubs, setSubtitle, formatSub, selectedLng, addMirroredGujaratiSub],
    );

    const updateSub = useCallback(
        (sub, obj) => {
            const index = hasSub(sub);
            if (index < 0) return;
            const subs = copySubs();
            const subClone = formatSub(sub);
            Object.assign(subClone, obj);
            subs[index] = subClone;
            setSubtitle(subs);
        },
        [hasSub, copySubs, setSubtitle, formatSub],
    );

    const updateSubWithTracking = useCallback(
        (sub, obj) => {
            const index = hasSub(sub);
            if (index < 0) return;
            updateSub(sub, obj);
            const trackingKey = `${index}-text-${obj.text2 !== undefined ? 'text2' : 'text'}`;
            if (!updateSubWithTracking.debouncedTracking) updateSubWithTracking.debouncedTracking = {};
            if (!updateSubWithTracking.debouncedTracking[trackingKey]) {
                updateSubWithTracking.debouncedTracking[trackingKey] = debounce(
                    (idx, field, oldVal, newVal) => EditTrackingService.trackEdit(idx, 'text', field, oldVal, newVal),
                    300,
                );
            }
            if (obj.text2 !== undefined && obj.text2 !== sub.text2) {
                updateSubWithTracking.debouncedTracking[trackingKey](index, 'text2', sub.text2, obj.text2);
            } else if (obj.text !== undefined && obj.text !== sub.text) {
                updateSubWithTracking.debouncedTracking[trackingKey](index, 'text', sub.text, obj.text);
            }
            if (obj.start !== undefined && obj.start !== sub.start) {
                EditTrackingService.trackEdit(index, 'timing', 'start', sub.start, obj.start);
            }
            if (obj.end !== undefined && obj.end !== sub.end) {
                EditTrackingService.trackEdit(index, 'timing', 'end', sub.end, obj.end);
            }
        },
        [hasSub, updateSub],
    );

    const updateSubIndex = useCallback(
        (sub, obj, index) => {
            const subs = copySubs();
            const subClone = subs[index];
            Object.assign(subClone, obj);
            if (subClone.check) {
                subs[index] = subClone;
                setSubtitle(subs);
            }
        },
        [copySubs, setSubtitle],
    );

    const mergeSub = useCallback(
        (sub) => {
            const index = hasSub(sub);
            if (index < 0) return;
            const subs = copySubs();
            const next = subs[index + 1];
            if (!next) return;
            let merge;
            if (!sub.text2) {
                merge = newSub({
                    start: sub.start, end: next.end,
                    text: sub.text.trim() + '\n' + next.text.trim(),
                    Estart: sub.Estart, Eend: next.Eend,
                    Etext: sub.Etext !== undefined && next.Etext !== undefined ? sub.Etext.trim() + '\n' + next.Etext.trim() : '',
                });
            } else {
                merge = newSub({
                    start: sub.start, end: next.end,
                    text: sub.text.trim() + '\n' + next.text.trim(),
                    text2: sub.text2.trim() + '\n' + next.text2.trim(),
                    Estart: sub.Estart, Eend: next.Eend,
                    Etext: sub.Etext !== undefined && next.Etext !== undefined ? sub.Etext.trim() + '\n' + next.Etext.trim() : '',
                    Etext2: sub.Etext2 !== undefined && next.Etext2 !== undefined ? sub.Etext2.trim() + '\n' + next.Etext2.trim() : '',
                });
            }
            subs[index] = merge;
            subs.splice(index + 1, 1);
            setSubtitle(subs);
            EditTrackingService.trackEdit(index, 'merge', 'subtitle',
                `${sub?.text2 || sub?.text || ''}\n${next?.text2 || next?.text || ''}`,
                merge?.text2 || merge?.text || '');
            setMirroredGujaratiSubs((prevGujaratiSubs) => {
                if (!prevGujaratiSubs || prevGujaratiSubs.length <= index + 1) return prevGujaratiSubs;
                const mergedGujarati = {
                    ...prevGujaratiSubs[index],
                    start: prevGujaratiSubs[index].start,
                    end: prevGujaratiSubs[index + 1].end,
                    text: (prevGujaratiSubs[index].text || '').trim() + '\n' + (prevGujaratiSubs[index + 1].text || '').trim(),
                    text2: (prevGujaratiSubs[index].text2 || '').trim() + '\n' + (prevGujaratiSubs[index + 1].text2 || '').trim(),
                    note: '', bookmark: false,
                };
                const newGujaratiSubs = [...prevGujaratiSubs];
                newGujaratiSubs[index] = mergedGujarati;
                newGujaratiSubs.splice(index + 1, 1);
                return newGujaratiSubs;
            });
        },
        [hasSub, copySubs, setSubtitle, newSub, setMirroredGujaratiSubs],
    );

    // V2: single Ctrl+Z registration + Ctrl+/ for shortcuts modal
    const onKeyDown = useCallback(
        (event) => {
            // Ctrl+/ or Ctrl+? — keyboard shortcuts modal
            if ((event.ctrlKey || event.metaKey) && event.key === '/') {
                event.preventDefault();
                setShowShortcutsModal((v) => !v);
                return;
            }

            const keyCode = getKeyCode(event);
            const KEY_TAB = 9;
            const KEY_Z = 90;

            switch (keyCode) {
                case KEY_TAB:
                    event.preventDefault();
                    if (player) {
                        if (player.element.paused) { player.element.play(); }
                        else { player.element.pause(); }
                    }
                    break;
                case KEY_Z:
                    if (event.metaKey || event.ctrlKey) {
                        event.preventDefault();
                        undoSubs();
                    }
                    break;
                default:
                    break;
            }
        },
        [player, undoSubs],
    );

    useMemo(() => {
        const currentSubIndex = subtitle.findIndex(
            (item) => item.startTime <= currentTime && item.endTime > currentTime,
        );
        if (currentSubIndex === -1) {
            const lastSubtitleBeforeGap = subtitle.reduce((closest, item, index) => {
                if (item.endTime <= currentTime && (closest === -1 || item.endTime > subtitle[closest].endTime)) return index;
                return closest;
            }, -1);
            if (lastSubtitleBeforeGap !== -1) setCurrentIndex(lastSubtitleBeforeGap);
        } else {
            setCurrentIndex(currentSubIndex);
        }
    }, [currentTime, subtitle]);

    const getLanguageVTTFile = async (language) => {
        const videoProps = JSON.parse(localStorage.getItem('videoProps'));
        if (!videoProps || !videoProps.videoLanguages) return '';
        const languageIndex = videoProps.videoLanguages.findIndex(
            (lang) => lang.languageName && lang.languageName === language,
        );
        if (languageIndex === -1) return '';
        return videoProps.videoLanguages[languageIndex].vttFileUrl || '';
    };

    const fetchData = async (selectedLangIndex) => {
        const videoProps = JSON.parse(localStorage.getItem('videoProps'));
        try {
            const vttData = await fetchBlobUrl(videoProps.videoLanguages[selectedLangIndex].vttFileUrl).catch(() => null);
            if (vttData) {
                videoProps.videoLanguages[selectedLangIndex].subtitleString = vttData;
                window.localStorage.setItem('videoProps', JSON.stringify(videoProps));
                return vttData;
            }
        } catch (_) {}
        return '';
    };

    const onLoad = useCallback(() => {
        // Read the currently active language from the ref so this works correctly
        // for any language including English-VO (which has its own VTT and index).
        const activeLng = selectedLngRef.current || 'English';
        const videoProps = JSON.parse(localStorage.getItem('videoProps'));

        // Resolve the actual index of activeLng in videoLanguages so we fetch the
        // correct VTT. Falling back to LanguageList.ENGLISH (0) was wrong for any
        // non-English language and caused English subtitles to overwrite the display.
        const selectedLangIndex = videoProps.videoLanguages.findIndex(
            (lang) => lang.languageName === activeLng,
        );
        const resolvedIndex = selectedLangIndex >= 0 ? selectedLangIndex : LanguageList.ENGLISH;

        // Show cached data immediately for a fast initial render…
        const localSubtitle = readSubtitleFromStorage(activeLng);
        if (localSubtitle && localSubtitle.length) {
            setSubtitle(localSubtitle.map((item) => new Sub(item)), false);
        }

        // …but ALWAYS follow up with the server fetch so that any previously
        // corrupted localStorage entry is replaced by the authoritative server copy.
        if (videoProps.videoLanguages[resolvedIndex].metadataCheckFlag.length > 0) {
            setBookmarked(videoProps.videoLanguages[resolvedIndex].metadataCheckFlag);
        }
        if (videoProps.videoLanguages[resolvedIndex].metadataComments.length > 0) {
            setSubtitleComment(videoProps.videoLanguages[resolvedIndex].metadataComments);
        }
        fetchData(resolvedIndex).then((subtitleData) => {
            if (subtitleData !== null) {
                const stateUpdated = getSubNodesfromVtt(subtitleData);
                if (stateUpdated && stateUpdated.length > 0) {
                    // Server copy is authoritative — apply it and write back to cache.
                    setSubtitle(stateUpdated.map((item) => new Sub(item)), false);
                }
            }
        });
    }, []);

    useEffect(() => {
        onLoad();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('onLoad', onLoad);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('onLoad', onLoad);
        };
    }, [onKeyDown, onLoad]);

    const processSearchMatches = useCallback(
        (term) => {
            if (!term || !subtitle) {
                setMatchOffsets([]);
                setCurrentMatchIndex(0);
                return;
            }
            const matches = [];
            const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(escapedTerm, 'gi');
            subtitle.forEach((item, index) => {
                if (!item.text2) return;
                let match;
                while ((match = regex.exec(item.text2)) !== null) {
                    matches.push({ textIndex: index, subtitleIndex: index, start: match.index, end: match.index + match[0].length });
                }
            });
            setMatchOffsets(matches);
            if (matches.length > 0) setCurrentMatchIndex(0);
        },
        [subtitle],
    );

    const focusMatchInTextarea = useCallback((matchIndex, startPos, endPos) => {
        const textareaId = `subtitleTextInput${matchIndex}`;
        const textarea = document.getElementById(textareaId);
        if (textarea) {
            textarea.parentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            textarea.focus();
            textarea.setSelectionRange(startPos, endPos);
        }
    }, []);

    const goToNextMatch = useCallback(() => {
        if (matchOffsets.length > 0) {
            const nextIndex = (currentMatchIndex + 1) % matchOffsets.length;
            setCurrentMatchIndex(nextIndex);
            const match = matchOffsets[nextIndex];
            if (match) focusMatchInTextarea(match.subtitleIndex, match.start, match.end);
        }
    }, [matchOffsets, currentMatchIndex, focusMatchInTextarea]);

    const goToPreviousMatch = useCallback(() => {
        if (matchOffsets.length > 0) {
            const prevIndex = (currentMatchIndex - 1 + matchOffsets.length) % matchOffsets.length;
            setCurrentMatchIndex(prevIndex);
            const match = matchOffsets[prevIndex];
            if (match) focusMatchInTextarea(match.subtitleIndex, match.start, match.end);
        }
    }, [matchOffsets, currentMatchIndex, focusMatchInTextarea]);

    useEffect(() => {
        const timer = setTimeout(() => processSearchMatches(searchTerm), 300);
        return () => clearTimeout(timer);
    }, [searchTerm, processSearchMatches]);

    useEffect(() => {
        if (matchOffsets.length > 0 && currentMatchIndex >= 0) {
            const match = matchOffsets[currentMatchIndex];
            if (match) setTimeout(() => focusMatchInTextarea(match.subtitleIndex, match.start, match.end), 100);
        }
    }, [matchOffsets, currentMatchIndex, focusMatchInTextarea]);

    const handleShowSearch = () => setShowSearch((prev) => !prev);
    const onSearchTermChange = (term) => { setSearchTerm(term); processSearchMatches(term); };
    const onReplaceTermChange = (term) => setReplaceTerm(term);

    const handleReplace = useCallback(() => {
        if (!searchTerm || matchOffsets.length === 0) return;
        const { textIndex, start, end } = matchOffsets[currentMatchIndex];
        const newSubtitles = [...subtitle];
        newSubtitles[textIndex] = {
            ...newSubtitles[textIndex],
            text2: newSubtitles[textIndex].text2.slice(0, start) + replaceTerm + newSubtitles[textIndex].text2.slice(end),
        };
        setSubtitle(newSubtitles);
        setTimeout(() => processSearchMatches(searchTerm), 0);
    }, [searchTerm, replaceTerm, matchOffsets, currentMatchIndex, subtitle, setSubtitle, processSearchMatches]);

    // V2: handleReplaceAll captures count and shows toast
    const handleReplaceAll = useCallback(() => {
        if (!searchTerm) return;
        const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(escapedTerm, 'gi');
        let count = 0;
        const newSubtitles = subtitle.map((item) => {
            if (!item.text2) return item;
            const newText = item.text2.replace(regex, () => { count++; return replaceTerm; });
            return { ...item, text2: newText };
        });
        setSubtitle(newSubtitles);
        setMatchOffsets([]);
        setCurrentMatchIndex(0);
        setReplaceAllCount(count);
        setShowReplaceToast(true);
    }, [searchTerm, replaceTerm, subtitle, setSubtitle]);

    // Seek to timestamp when arriving from SRT search result click
    useEffect(() => {
        const seekToTime = location?.state?.seekToTime;
        if (!player || seekToTime == null) return;
        const doSeek = () => {
            if (player.updateTime) {
                player.updateTime(seekToTime);
            } else if (player.element) {
                player.element.currentTime = seekToTime;
            }
        };
        if (player.element && player.element.readyState >= 1) {
            doSeek();
        } else if (player.element) {
            player.element.addEventListener('loadedmetadata', doSeek, { once: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [player]);

    const handleSetTime = (timeInSeconds) => {
        if (player?.updateTime) {
            player.updateTime(timeInSeconds);
        } else if (player?.element) {
            player.element.currentTime = timeInSeconds;
        }
    };

    const determineUserLevel = useCallback((videoProps, selectedLng) => {
        if (!videoProps || !selectedLng) return '';
        const selectedLanguage = videoProps.videoLanguages.find((lang) => lang.languageName === selectedLng);
        if (!selectedLanguage) return 'Level 1';
        switch (selectedLanguage.processStage) {
            case 'LEVEL0_AI_COMPLETE': return 'Level 1';
            case 'LEVEL2_WIP': case 'LEVEL1_SEVAK_COMPLETE': return 'Level 2';
            // The next level to work on: after L2 is submitted, and once Published, that's Level 3
            case 'LEVEL2_SEVAK_COMPLETE': case 'LEVEL3_WIP': case 'LEVEL3_SEVAK_COMPLETE': case 'PUBLISHED': return 'Level 3';
            default: return 'Level 1';
        }
    }, []);

    const sessionStartingToTrackRef = useRef(false);
    useEffect(() => {
        const startTracking = async () => {
            const videoProps = JSON.parse(localStorage.getItem('videoProps'));
            if (videoProps && selectedLng) {
                const level = determineUserLevel(videoProps, selectedLng);
                setUserLevel(level);
                const session = await EditTrackingService.startSession(videoProps.id, selectedLng, level, 'V2');
                if (session) { setSessionActive(true); }
                sessionStartingToTrackRef.current = false;
            }
        };
        if (selectedLng && !sessionStartingToTrackRef.current) {
            sessionStartingToTrackRef.current = true;
            startTracking();
        }
        return () => { EditTrackingService.endSession(); sessionStartingToTrackRef.current = false; };
    }, [selectedLng, determineUserLevel]);

    const highlightSearchTerm = useCallback(
        (text, searchTermArg, index) => {
            if (!text || !searchTermArg) return text || '';
            try {
                const escapedSearchTerm = searchTermArg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
                return text.replace(regex, (match, group, offset) => {
                    const isCurrentMatch = (matchOffsets || []).some(
                        (m) => m && m.textIndex === index && m.start <= offset && offset < m.end &&
                            currentMatchIndex === (matchOffsets || []).indexOf(m),
                    );
                    return isCurrentMatch ? `<mark class="current">${match}</mark>` : `<mark>${match}</mark>`;
                });
            } catch (_) { return text; }
        },
        [matchOffsets, currentMatchIndex],
    );

    const props = {
        selectedLng, setSelectedLng, lngIndex, setLngIndex,
        leftColumnLanguage, setLeftColumnLanguage,
        rightColumnLanguage, setRightColumnLanguage,
        showDualColumns, setShowDualColumns,
        showSearch, setShowSearch, handleShowSearch,
        player, setPlayer, subtitle, setSubtitle,
        waveform, setWaveform, currentTime, setCurrentTime,
        currentIndex, setCurrentIndex,
        playing, setPlaying, language, setLanguage,
        loading, setLoading, setProcessing,
        subtitleHistory, translate, setTranslate,
        directUrl, setDirectUrl, viewEng, setViewEng,
        bookmarked, setBookmarked,
        subtitleComment, setSubtitleComment,
        notify, newSub, hasSub, checkSub,
        removeSub, addSub, undoSubs, clearSubs,
        updateSub: updateSubWithTracking,
        updateSubIndex, formatSub, mergeSub,
        setLanguageSubTitle, readOnly, duplicateSessionDetected,
        handleSetTime, userLevel,
        saveMirroredGujaratiVtt, splitMirroredGujratiSubtitle,
    };

    return (
        <>
            <GlobalStyle />
            <StyleV2 onMouseMove={handleUserActivity} onClick={handleUserActivity} onKeyDown={handleUserActivity}>
                {Date.now() - lastActivity > INACTIVITY_TIMEOUT - 60000 && (
                    <div className="inactivity-warning-banner">
                        <Alert severity="warning">
                            Your session will expire in less than 1 minute due to inactivity.
                        </Alert>
                    </div>
                )}
                {readOnly && (
                    <div className="read-only-banner">
                        <Alert
                            severity="error"
                            icon={<LockIcon fontSize="large" />}
                            sx={{
                                fontSize: '1.1rem',
                                fontWeight: 700,
                                alignItems: 'center',
                                py: 1.5,
                                '& .MuiAlert-icon': { fontSize: '2rem' },
                            }}
                        >
                            {activeEditor && activeEditor.startsWith('The editing session')
                                ? `READ-ONLY MODE: ${activeEditor}`
                                : `READ-ONLY MODE: This subtitle is currently being edited by ${activeEditor}. You can view but cannot make changes.`}
                        </Alert>
                    </div>
                )}

                {/* Toolbar */}
                <div className="toolbar-container">
                    <Header
                        {...props}
                        showSearch={showSearch}
                        handleShowSearch={handleShowSearch}
                        searchTerm={searchTerm}
                        replaceTerm={replaceTerm}
                        onSearchTermChange={onSearchTermChange}
                        onReplaceTermChange={onReplaceTermChange}
                        matchOffsets={matchOffsets}
                        currentMatchIndex={currentMatchIndex}
                        goToNextMatch={goToNextMatch}
                        goToPreviousMatch={goToPreviousMatch}
                        handleReplace={handleReplace}
                        handleReplaceAll={handleReplaceAll}
                    />
                    {/* V2: Validation badge — overlaps only */}
                    <ValidationBadge
                        subtitle={subtitle}
                        checkSub={checkSub}
                    />
                </div>

                {/* V2: Three-panel resizable layout */}
                <div className="v2-layout-wrapper">
                    <EditorLayout
                        videoPanel={
                            <div id="subtitle-editor-video-panel" style={{ height: '100%' }}>
                                <VideoPanel
                                    {...props}
                                    followPlayhead={followPlayhead}
                                    setFollowPlayhead={setFollowPlayhead}
                                />
                            </div>
                        }
                        subtitlePanel={
                            <div id="subtitle-editor-subtitle-panel" style={{ height: '100%' }}>
                                <SubtitlesV2
                                    {...props}
                                    searchTerm={searchTerm}
                                    matchOffsets={matchOffsets || []}
                                    currentMatchIndex={currentMatchIndex}
                                    highlightSearchTerm={highlightSearchTerm}
                                    followPlayhead={followPlayhead}
                                    addSub={addSub}
                                    newSub={newSub}
                                />
                            </div>
                        }
                        timelinePanel={<div id="subtitle-editor-timeline-panel" style={{ height: '100%' }}><Footer {...props} /></div>}
                    />
                </div>

                {/* V2: Keyboard shortcuts modal */}
                <KeyboardShortcutsModal
                    open={showShortcutsModal}
                    onClose={() => setShowShortcutsModal(false)}
                />

                {/* V2: Step-by-step help overlay */}
                <HelpOverlay
                    helpItems={subtitleEditorHelpItems}
                    title="Subtitle Editor Guide"
                    buttonId="help-subtitle-editor"
                />

                {/* V2: Replace-all feedback toast */}
                <Snackbar
                    open={showReplaceToast}
                    autoHideDuration={3000}
                    onClose={() => setShowReplaceToast(false)}
                    message={`Replaced ${replaceAllCount} occurrence${replaceAllCount !== 1 ? 's' : ''}`}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                />

                {loading ? <Loading loading={loading} /> : null}
                {processing > 0 && processing < 100 ? <ProgressBar processing={processing} /> : null}
                <NotificationSystem ref={notificationSystem} allowHTML={true} />
            </StyleV2>
        </>
    );
}
